import dayjs from 'dayjs';
import {
  CanAccess,
  CrudFilters,
  HttpError,
  useNotification
} from '@refinedev/core';
import { CorporateReportFilterForm } from './components/CorporateReportFilterForm';
import { DatePicker, Form, Input, Select, Table } from 'antd';
import { ENROLLMENT_TYPE_OPTIONS } from './constants';
import { EnrollmentStatusCell } from './components/EnrollmentStatusCell';
import { ExportButton, TextField, useTable } from '@refinedev/antd';
import { IEnrolledStudent } from 'interfaces/corporate_reports';
import { ListLayout } from 'components/layout/layout/ListLayout';
import { StudentInfoCell } from './components/StudentInfoCell';
import { formatDate } from 'services/date';
import {
  normalizeFilterText,
  validateEnrollmentTypeSelection
} from './utils/filters';
import { useCallback, useEffect, useState } from 'react';
import { useCorporateCourseOptions } from './hooks/useCorporateCourseOptions';
import { useCorporateReportExport } from './hooks/useCorporateReportExport';

interface IFilter {
  by_course_enrollment_type?: string;
  by_course_id?: string[];
  by_user_name?: string;
  by_user_email?: string;
  by_range_date?: [dayjs.Dayjs, dayjs.Dayjs];
}

const REPORT_STORAGE_KEY = 'enrolled_students_id';
const getEnrollmentTypeLabel = (value?: string) =>
  ENROLLMENT_TYPE_OPTIONS.find(option => option.value === value)?.label ||
  value ||
  '-';

export const EnrolledStudents = () => {
  const [hasSearched, setHasSearched] = useState(false);
  const notification = useNotification();
  const { RangePicker } = DatePicker;

  const { tableProps, searchFormProps } = useTable<
    IEnrolledStudent,
    HttpError,
    IFilter
  >({
    resource: 'enrolled_students',
    queryOptions: { enabled: hasSearched },
    onSearch: params => {
      return [
        {
          field: 'filter[by_course_id][]',
          operator: 'in',
          value: params.by_course_id
        },
        {
          field: 'filter[by_course_enrollment_type]',
          operator: 'eq',
          value: params.by_course_enrollment_type
        },
        {
          field: 'filter[by_user_name]',
          operator: 'eq',
          value: normalizeFilterText(params.by_user_name)
        },
        {
          field: 'filter[by_user_email]',
          operator: 'eq',
          value: normalizeFilterText(params.by_user_email)
        },
        {
          field: 'filter[by_range_date][]',
          operator: 'between',
          value: [
            params.by_range_date?.[0]?.format('YYYY-MM-DD'),
            params.by_range_date?.[1]?.format('YYYY-MM-DD')
          ]
        }
      ] as CrudFilters;
    },
    sorters: {
      mode: 'server'
    }
  });
  const selectedEnrollmentType = Form.useWatch(
    'by_course_enrollment_type',
    searchFormProps.form
  );

  const tablePropsWithConditionalData = hasSearched
    ? tableProps
    : { ...tableProps, dataSource: [], loading: false };
  const { courseOptions } = useCorporateCourseOptions(selectedEnrollmentType);

  useEffect(() => {
    searchFormProps.form?.setFieldValue('by_course_id');
  }, [searchFormProps.form, selectedEnrollmentType]);

  const { reportId, handleExportReport } = useCorporateReportExport<IFilter>({
    storageKey: REPORT_STORAGE_KEY,
    reportPath: '/admin/enrolled_students/report',
    searchFormProps,
    notification,
    buildExportFilters: formValues => ({
      by_course_id: formValues.by_course_id,
      by_course_enrollment_type: formValues.by_course_enrollment_type,
      by_user_name: normalizeFilterText(formValues.by_user_name),
      by_user_email: normalizeFilterText(formValues.by_user_email),
      by_range_date: formValues.by_range_date
    })
  });

  const validateFilter = useCallback(() => {
    return validateEnrollmentTypeSelection(
      searchFormProps.form?.getFieldValue('by_course_enrollment_type'),
      () =>
        notification.open?.({
          type: 'error',
          message: 'Modalidade obrigatória',
          description: 'Selecione a modalidade para aplicar o filtro.'
        })
    );
  }, [notification, searchFormProps.form]);

  return (
    <ListLayout
      title="Alunos Inscritos"
      headerActions={
        <CanAccess resource="corporate_reports/enrolled_students" action="list">
          <ExportButton
            onClick={handleExportReport}
            disabled={!!reportId || !hasSearched || !selectedEnrollmentType}
            loading={!!reportId}
          >
            {!!reportId ? 'Relatório está sendo gerado' : 'Exportar'}
          </ExportButton>
        </CanAccess>
      }
    >
      <CorporateReportFilterForm
        searchFormProps={searchFormProps}
        tableLoading={!!tableProps.loading}
        setHasSearched={setHasSearched}
        onValidateFilter={validateFilter}
      >
        <Form.Item className="mb-0 w-45" help={null}>
          <Form.Item
            name="by_course_enrollment_type"
            noStyle
            rules={[{ required: true, message: '' }]}
          >
            <Select
              allowClear
              placeholder="Modalidade"
              options={ENROLLMENT_TYPE_OPTIONS}
            />
          </Form.Item>
        </Form.Item>
        <Form.Item name="by_course_id" className="mb-0 w-45">
          <Select
            mode="multiple"
            allowClear
            disabled={!selectedEnrollmentType}
            style={{ width: '100%' }}
            placeholder="Curso"
            {...courseOptions}
          />
        </Form.Item>
        <Form.Item name="by_user_name" className="mb-0 w-56">
          <Input placeholder="Nome" allowClear />
        </Form.Item>
        <Form.Item name="by_user_email" className="mb-0 w-56">
          <Input placeholder="E-mail" allowClear />
        </Form.Item>
        <Form.Item name="by_range_date">
          <RangePicker format="DD/MM/YYYY" />
        </Form.Item>
      </CorporateReportFilterForm>

      <Table
        {...tablePropsWithConditionalData}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{
          ...tablePropsWithConditionalData.pagination,
          showSizeChanger: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} itens`
        }}
      >
        <Table.Column
          dataIndex="user_name"
          key="user_name"
          title="Informações do aluno"
          sorter
          className="min-w-[180px] max-w-[250px]"
          render={(_, record) => (
            <StudentInfoCell
              userName={record.user_name}
              userEmail={record.user_email}
            />
          )}
        />
        <Table.Column
          dataIndex="user_document"
          key="user_document"
          title="CPF"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="user_phone"
          key="user_phone"
          title="Telefone"
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="course_enrollment_type"
          key="course_enrollment_type"
          title="Modalidade"
          render={value => <TextField value={getEnrollmentTypeLabel(value)} />}
        />
        <Table.Column
          dataIndex="course_name"
          key="course_name"
          title="Curso"
          sorter
          render={value => <TextField value={value} />}
        />
        <Table.Column
          dataIndex="enrollment_status"
          key="enrollment_status"
          title="Situação da matrícula"
          width={180}
          render={(value, record) => (
            <EnrollmentStatusCell
              enrollmentRegistration={record.enrollment_registration}
              enrollmentStatus={value}
            />
          )}
        />
        <Table.Column
          dataIndex="created_at"
          key="created_at"
          title="Data de início"
          sorter
          render={value => (
            <TextField value={formatDate(value, 'DD/MM/YYYY')} />
          )}
        />
      </Table>
    </ListLayout>
  );
};
